import { Directive, ElementRef, HostListener, Input, AfterViewInit, OnDestroy, NgZone, OnInit, Output, EventEmitter } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AppUtilityService } from '../app.service';


// enable gridster item only if it is visible on screen
@Directive({
	selector: '[appGridsterLazyLoad]',
	standalone: false
})
export class GridsterLazyLoadDirective implements AfterViewInit, OnDestroy {
	@Input('appGridsterLazyLoad') gstItems: any;
	@Input() monitorInterval: number = 500;

	showLog: boolean = false;
	private allVisible: boolean = false;
	private checking: boolean = false;
	private listItemCount: number = 0;
	private changes: MutationObserver;

	constructor(
		private el: ElementRef,
		private router: Router
	) {
		// if url change, reset 
		this.router.events.subscribe(
			(event: any) => {
				if (event instanceof NavigationEnd) {
					this.allVisible = false;
					this.listItemCount = 0;
					this.doCecking();
				}
			}
		);

		this.changes = new MutationObserver((mutations: MutationRecord[]) => {
			// detect dom changes
			if (this.showLog) {
				console.log('appGridsterLazyLoad Dom chnage');
				console.log(mutations);
			}
			this.allVisible = false;
			this.listItemCount = 0;
			this.doCecking();
		});

		this.changes.observe(el.nativeElement, {
			childList: true
		});
	}

	ngOnDestroy(): void {
		this.changes.disconnect();
	}

	ngAfterViewInit(): void {
		this.doCecking();
	}

	private checkVisible() {
		let list = this.el.nativeElement.getElementsByTagName("gridster-item");
		if (list == null || (list.length != null && list.length === 0)) {
			return;
		}
		/* if somehow the list item count change, reset the allVisible */
		if (this.listItemCount !== list.length) {
			this.allVisible = false;
		}
		if (!this.allVisible) {
			let visibleLoop = true;
			for (let i = 0; i < list.length; i++) {
				let id = list[i].id;
				if (id != null && id.trim().length > 0) {
					let visible: boolean = AppUtilityService.isVisibleOnScreen(list[i]);
					if (this.showLog) { console.log("gridster item:" + id + " (visible:" + visible + ")"); }
					if (visible) {
						try {
							this.gstItems[id] = true;
						}
						catch (ex) {
							console.log("fail to enable gridster item: " + id);
						}
					}
					else {
						// already load? do nothing
						if (this.gstItems[id] == null || this.gstItems[id] === false) {
							visibleLoop = false;
						}
					}
				}
			}
			this.listItemCount = list.length;
			this.allVisible = visibleLoop;
			if (this.showLog) { console.log("this.allVisible:" + this.allVisible); }
		}
	}

	private doCecking() {
		if (!this.checking) {
			this.checking = true;
			setTimeout(() => {
				this.checkVisible();
				this.checking = false;
			}, this.monitorInterval);
		}
	}

	@HostListener('window:resize') onResize() {
		if (this.showLog) { console.log('appGridsterLazyLoad resize'); }
		this.doCecking();
	}

	@HostListener('window:scroll') onScroll() {
		if (this.showLog) { console.log('appGridsterLazyLoad scroll'); }
		this.doCecking();
	}

	@HostListener('wheel') onWheel() {
		if (this.showLog) { console.log('appGridsterLazyLoad wheel'); }
		this.doCecking();
	}

	@HostListener('click') onClick() {
		if (this.showLog) { console.log('appGridsterLazyLoad click'); }
		this.doCecking();
	}

	@HostListener('load') onLoad() {
		if (this.showLog) { console.log('appGridsterLazyLoad load'); }
		this.doCecking();
	}
}


// check image file, if not exist, replace with blank
@Directive({
	selector: '[appImgCheck]',
	standalone: false
})
export class ImgCheckDirective implements AfterViewInit {
	@Input() defaultImage: string;

	constructor(private el: ElementRef) {
		//console.log('appImgCheck');
	}

	ngAfterViewInit(): void {
		this.defaultImage = this.defaultImage == null || this.defaultImage === "" ? "assets/images/blank.png" : this.defaultImage.trim();
	}

	@HostListener('load') onLoad() {
	}

	@HostListener('error') onError() {
		// avoid endless errors, stop error handle if it is default image
		if (this.el.nativeElement.src.indexOf(this.defaultImage.toLowerCase()) < 0) {
			this.el.nativeElement.src = this.defaultImage; // set default image
		}
	}
}


// not load the card image until it is visible on screen
@Directive({
	selector: '[appCardImgLazyLoad]',
	standalone: false
})
export class CardImgLazyLoadDirective implements AfterViewInit {
	@Input() imgSrc: string;

	observer!: IntersectionObserver;
	myImage: any = null;
	defaultImage = 'assets/images/blankCard.png';
	defaultSrc = 'assets/images/blankCardProgress.gif';
	alreadyDownload: boolean = false;
	checking: boolean = false;

	constructor(
		private el: ElementRef,
		private ngZone: NgZone
	) {
		this.myImage = this.el.nativeElement;
		this.imgSrc = (this.imgSrc ?? "") === "" ? this.defaultImage : this.imgSrc.trim();
	}

	@HostListener('error') onError() {
		if (this.alreadyDownload) {
			return;
		}
		this.myImage.style.display = 'none';
		this.myImage.src = this.defaultImage;
		this.myImage.style.display = 'unset';
	}

	ngAfterViewInit(): void {
		this.ngZone.runOutsideAngular(() => {
			this.observer = new IntersectionObserver((entries) => {
				entries.forEach((e) => {
					if (e.isIntersecting && !this.alreadyDownload && !this.checking) {
						this.checking = true;
						if (!this.alreadyDownload) {
							this.myImage.src = this.imgSrc;
							this.alreadyDownload = true;
						}
						this.checking = false;
					}
					else if ((this.myImage.src ?? "") === "") {
						this.myImage.src = this.defaultSrc;
					}
				});
			});
			this.observer.observe(this.el.nativeElement);
		});
	}

	ngOnDestroy(): void {
		this.observer.disconnect();
	}
}


// hide the naviagtion arrow on the first and last slide
import { NgbCarousel, NgbSlideEvent } from '@ng-bootstrap/ng-bootstrap';
@Directive({
	selector: '[appCarouselNoWrap]',
	standalone: false
})
export class CarouselNoWrapDirective implements AfterViewInit, OnDestroy {
	private showLog: boolean = false;
	private currentSlide: number = 0;
	private totalSlides: number = 0;
	private changes: MutationObserver;

	constructor(
		private el: ElementRef,
		private carousel: NgbCarousel
	) {
		carousel.interval = 0;
		carousel.showNavigationArrows = true;
		carousel.showNavigationIndicators = false;
		carousel.wrap = false;

		this.changes = new MutationObserver((mutations: MutationRecord[]) => {
			// detect dom changes
			if (this.showLog) {
				console.log('appCarouselNoWrap Dom chnage');
				console.log(mutations);
			}
			
			if (this.carousel != null && this.carousel.slides != null) {
				// if lenght increase, enable right arrow on the last slide
				if (this.carousel.slides.length > this.totalSlides) {
					if (this.currentSlide === this.totalSlides - 1) {
						this.toggleaCarouselNavButton(false, true);
					}
				}
				//if length reduce 
				else if (Number.isNaN(Number(this.carousel.activeId)) == false && this.carousel.slides.length < this.totalSlides) {
					this.updateNav(this.carousel.activeId);
				}
			}
		});

		this.changes.observe(el.nativeElement, { childList: true, subtree:true });
	}

	ngOnDestroy(): void {
		this.changes.disconnect();
	}

	ngAfterViewInit(): void {
		try {
			if (this.showLog) {
				console.log("appCarouselNoWrap loaed");
			}
			// disable the prev on the first
			this.toggleaCarouselNavButton(true, false);
			if (this.showLog) {
				console.log(this.carousel.slides.length??0);
			}
			// if there is one slide only, disable the next
			if ((this.carousel.slides.length ?? 0) == 1) {
				this.toggleaCarouselNavButton(false, false);
			}
		}
		catch (ex) {
			console.log(ex);
		}
	}

	private toggleaCarouselNavButton(isPrev: boolean, enable: boolean): void {
		try {
			let ac = isPrev ? "carousel-control-prev" : "carousel-control-next";
			let items = this.el.nativeElement.getElementsByClassName("carousel-item") as HTMLCollectionOf<HTMLElement>;
			let nav = this.el.nativeElement.getElementsByClassName(ac) as HTMLCollectionOf<HTMLElement>;
			if ((items?.length ?? 0) > 0 && (nav?.length ?? 0) > 0) {
				nav[0].style.display = enable == true ? "flex" : "none";
			}
		}
		catch (ex) {
			console.log(ex);
		}
	}

	private updateNav(activeId: string): void {
		this.currentSlide = parseInt(activeId);
		this.totalSlides = this.carousel.slides.length ?? 0;

		if (this.showLog) {
			console.log(this.currentSlide);
			console.log(this.totalSlides);
		}

		// disable the prev button on the first page
		if (this.currentSlide === 0) {
			this.toggleaCarouselNavButton(true, false);
		}
		else {
			this.toggleaCarouselNavButton(true, true);
		}

		// disable the next button on last page
		if (this.currentSlide === this.totalSlides - 1) {
			this.toggleaCarouselNavButton(false, false);
		}
		else {
			this.toggleaCarouselNavButton(false, true);
		}
	}

	@HostListener('slide', ['$event'])
	onSlide(e: NgbSlideEvent) {
		if (e == null || e.current == null) {
			return;
		}
		if (this.showLog) { console.log(e); }

		//if current not a number, throw error, need statement below as example to get this directive work
		// ngbSlide *ngFor="let page of pages; let i=index" id="{{i}}"
		if (Number.isNaN(Number(e.current))) {
			throw "please assign an id to each slide to get appCarouselNavCtrl work";
		}

		this.updateNav(parseInt(e.current.split("-").pop()).toString());
	}
}

// check an object visibility
// example <div appTrackVisibility visibile = "onVisibile($event)" ></div> 
@Directive({
	selector: '[appTrackVisibility]',
	standalone: false
})
export class TrackVisibilityDirective implements OnInit, OnDestroy {
	observer!: IntersectionObserver;

	@Output() visibile = new EventEmitter<boolean>();

	constructor(private el: ElementRef<HTMLElement>, private ngZone: NgZone) { }

	ngOnInit(): void {
		this.ngZone.runOutsideAngular(() => {
			this.observer = new IntersectionObserver((entries) => {
				entries.forEach((e) => {
					this.visibile.emit(e.isIntersecting);
				});
			});
			this.observer.observe(this.el.nativeElement);
		});
	}

	ngOnDestroy(): void {
		this.observer.disconnect();
	}
}

// AlphaNumericOnly input box
@Directive({
	selector: '[appAlphaNumeric]',
	standalone: false
})
export class AlphaNumericDirective {
	@HostListener('input', ['$event'])
	onInput(event: Event) {
		const input = event.target as HTMLInputElement;
		const originalValue = input.value;

		// Remove any non-alphanumeric characters
		const newValue = originalValue.replace(/[^a-zA-Z0-9]/g, ''); // Allows only alphanumeric characters

		// Update the input value if it has changed
		if (originalValue !== newValue) {
			input.value = newValue;
			// Trigger input event to ensure Angular's change detection is aware of the change
			input.dispatchEvent(new Event('input'));
		}
	}
}

//AlphaNumeric space period
@Directive({
	selector: '[appAlphaNumericSpacePeriod]',
	standalone: false
})
export class AlphaNumericSpacePeriodDirective {
	@HostListener('input', ['$event'])
	onInput(event: Event) {
		const input = event.target as HTMLInputElement;
		const originalValue = input.value;

		// Remove any non-alphanumeric characters
		const newValue = originalValue.replace(/[^a-zA-Z0-9 .]/g, ''); // Allows alphanumeric space period

		// Update the input value if it has changed
		if (originalValue !== newValue) {
			input.value = newValue;
			// Trigger input event to ensure Angular's change detection is aware of the change
			input.dispatchEvent(new Event('input'));
		}
	}
}
